import React, { useState } from "react";
import { motion } from "framer-motion";
import { toast } from "@/components/ui/sonner";
import { api } from "@/lib/api";
import { Mail, Loader2, Check } from "lucide-react";

export default function Newsletter() {
  const [email, setEmail] = useState("");
  const [name, setName] = useState("");
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (!email) return;
    setLoading(true);
    try {
      const { data } = await api.post("/newsletter", { email, name });
      toast.success(data.message || "Subscribed!");
      setEmail(""); setName("");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Something went wrong.");
    } finally {
      setLoading(false);
    }
  };

  const trust = ["No spam", "Unsubscribe anytime", "Privacy respected"];

  return (
    <section id="newsletter" className="py-24 sm:py-32">
      <div className="max-w-5xl mx-auto px-6 sm:px-8">
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.7 }}
          className="relative overflow-hidden rounded-[2.5rem] bg-[#0A0A0A] text-white px-7 sm:px-16 py-14 sm:py-20 grain"
        >
          <div className="absolute -top-20 -right-16 w-72 h-72 rounded-full blob"
               style={{ background: "radial-gradient(circle, rgba(212,175,55,0.35), transparent 70%)" }} />
          <div className="relative z-10 text-center max-w-2xl mx-auto">
            <span className="inline-grid place-items-center h-14 w-14 rounded-2xl bg-gold text-black mb-6">
              <Mail size={26} />
            </span>
            <h2 className="font-display text-4xl sm:text-5xl font-bold tracking-tight">Join Kate's Reader Community</h2>
            <p className="text-gray-300 mt-5 leading-relaxed">
              Receive updates on new book releases, exclusive chapters, practical study advice, AI insights and
              behind-the-scenes content before anyone else.
            </p>

            <form onSubmit={submit} className="mt-9 grid gap-3 sm:grid-cols-[1fr_1.3fr_auto] sm:items-center max-w-2xl mx-auto" data-testid="newsletter-form">
              <input
                type="text"
                placeholder="First name"
                value={name}
                onChange={(e) => setName(e.target.value)}
                data-testid="newsletter-name"
                className="h-[52px] rounded-full bg-white/10 border border-white/15 px-5 text-sm placeholder:text-gray-400 focus:outline-none focus:border-gold transition-colors"
              />
              <input
                type="email"
                required
                placeholder="Email address"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                data-testid="newsletter-email"
                className="h-[52px] rounded-full bg-white/10 border border-white/15 px-5 text-sm placeholder:text-gray-400 focus:outline-none focus:border-gold transition-colors"
              />
              <button
                type="submit"
                disabled={loading}
                data-testid="newsletter-submit"
                className="btn-gold h-[52px] rounded-full px-8 text-sm font-semibold inline-flex items-center justify-center gap-2 disabled:opacity-70 w-full sm:w-auto"
              >
                {loading ? <Loader2 className="animate-spin" size={16} /> : "Subscribe"}
              </button>
            </form>

            <div className="mt-7 flex flex-wrap items-center justify-center gap-x-7 gap-y-2">
              {trust.map((t) => (
                <span key={t} className="inline-flex items-center gap-1.5 text-xs text-gray-400">
                  <Check size={14} className="text-gold" strokeWidth={3} /> {t}
                </span>
              ))}
            </div>
          </div>
        </motion.div>
      </div>
    </section>
  );
}
